import { useState } from 'react';
import { Link } from 'react-router-dom';
import { expressionIdeasQuestions } from './data/expressionIdeasQuestions';
import { useScoreHistory } from '../../../hooks/useScoreHistory';

export default function ExpressionIdeasPracticePage() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [answers, setAnswers] = useState<(number | null)[]>(
    new Array(expressionIdeasQuestions.length).fill(null)
  );
  const [isComplete, setIsComplete] = useState(false);
  const { saveScore } = useScoreHistory();

  const question = expressionIdeasQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / expressionIdeasQuestions.length) * 100;

  const handleAnswerSelect = (answerIndex: number) => {
    if (showExplanation) return;
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;
    
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = selectedAnswer;
    setAnswers(newAnswers);
    setShowExplanation(true);
  };

  const handleNext = () => {
    if (currentQuestion < expressionIdeasQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(answers[currentQuestion + 1]);
      setShowExplanation(answers[currentQuestion + 1] !== null);
    } else {
      // Calculate and save score
      const correctCount = calculateScore();
      const percentage = Math.round((correctCount / expressionIdeasQuestions.length) * 100);
      
      saveScore({
        examName: 'SAT R&W: Expression of Ideas',
        domain: 'Expression of Ideas',
        examType: 'reading-writing',
        score: correctCount,
        total: expressionIdeasQuestions.length,
        percentage,
      });
      
      setIsComplete(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedAnswer(answers[currentQuestion - 1]);
      setShowExplanation(answers[currentQuestion - 1] !== null);
    }
  };

  const calculateScore = () => {
    let correct = 0;
    answers.forEach((answer, index) => {
      if (answer === expressionIdeasQuestions[index].correctAnswer) {
        correct++;
      }
    });
    return correct;
  };

  const handleTryAgain = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setAnswers(new Array(expressionIdeasQuestions.length).fill(null));
    setIsComplete(false);
  };

  if (isComplete) {
    const score = calculateScore();
    const percentage = Math.round((score / expressionIdeasQuestions.length) * 100);

    return (
      <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 via-white to-purple-50">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-fuchsia-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-trophy-line text-4xl text-white"></i>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Practice Complete!</h1>
              <p className="text-xl text-gray-600">Expression of Ideas</p>
            </div>

            <div className="bg-gradient-to-br from-fuchsia-50 to-purple-50 rounded-xl p-8 mb-8">
              <div className="text-center mb-6">
                <div className="text-6xl font-bold text-fuchsia-600 mb-2">{score}/{expressionIdeasQuestions.length}</div>
                <div className="text-2xl font-semibold text-gray-700">{percentage}% Correct</div>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-4 mb-6">
                <div
                  className="bg-gradient-to-r from-fuchsia-500 to-purple-500 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-white rounded-lg p-4">
                  <div className="text-3xl font-bold text-green-600">{score}</div>
                  <div className="text-sm text-gray-600">Correct</div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="text-3xl font-bold text-red-600">{expressionIdeasQuestions.length - score}</div>
                  <div className="text-sm text-gray-600">Incorrect</div>
                </div>
              </div>
            </div>

            <div className="bg-fuchsia-50 border-l-4 border-fuchsia-500 p-6 rounded-lg mb-8">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-lightbulb-line text-fuchsia-600"></i>
                Performance Analysis
              </h3>
              <p className="text-gray-700">
                {percentage >= 80 && "Excellent work! You have strong skills in transitions and rhetorical synthesis."}
                {percentage >= 60 && percentage < 80 && "Good job! Review the explanations to improve your understanding of effective transitions and sentence combinations."}
                {percentage < 60 && "Keep practicing! Focus on choosing logical transitions and combining sentences effectively while maintaining clarity."}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleTryAgain}
                className="flex-1 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white px-8 py-4 rounded-xl font-semibold hover:from-fuchsia-600 hover:to-purple-600 transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-2"></i>
                Try Again
              </button>
              <Link
                to="/sat-prep"
                className="flex-1 bg-gray-100 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 text-center whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to SAT Prep
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 via-white to-purple-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/sat-prep"
            className="inline-flex items-center gap-2 text-fuchsia-600 hover:text-fuchsia-700 font-medium mb-4 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to SAT Prep
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Expression of Ideas Practice
          </h1>
          <p className="text-gray-600">
            Master transitions and rhetorical synthesis
          </p>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700">
              Question {currentQuestion + 1} of {expressionIdeasQuestions.length}
            </span>
            <span className="text-sm font-semibold text-fuchsia-600">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-fuchsia-500 to-purple-500 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-xl shadow-xl p-6 md:p-8 mb-6">
          {/* Subtopic Badge */}
          <div className="inline-block bg-fuchsia-100 text-fuchsia-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            {question.subtopic}
          </div>

          {/* Passage */}
          <div className="bg-gray-50 border-l-4 border-fuchsia-500 p-6 rounded-lg mb-6">
            <p className="text-gray-800 leading-relaxed whitespace-pre-line">{question.passage}</p>
          </div>

          {/* Question */}
          <h3 className="text-xl font-bold text-gray-900 mb-6">{question.question}</h3>

          {/* Answer Choices */}
          <div className="space-y-3 mb-6">
            {question.choices.map((choice, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrect = index === question.correctAnswer;
              const showResult = showExplanation;

              let buttonClass = "w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ";
              
              if (showResult) {
                if (isCorrect) {
                  buttonClass += "border-green-500 bg-green-50 ";
                } else if (isSelected && !isCorrect) {
                  buttonClass += "border-red-500 bg-red-50 ";
                } else {
                  buttonClass += "border-gray-200 bg-gray-50 ";
                }
              } else {
                if (isSelected) {
                  buttonClass += "border-fuchsia-500 bg-fuchsia-50 ";
                } else {
                  buttonClass += "border-gray-200 hover:border-fuchsia-300 hover:bg-fuchsia-50 cursor-pointer ";
                }
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={showExplanation}
                  className={buttonClass}
                >
                  <div className="flex items-start gap-3">
                    <span className="font-bold text-gray-700 min-w-[24px]">
                      {String.fromCharCode(65 + index)}.
                    </span>
                    <span className="text-gray-800 flex-1">{choice}</span>
                    {showResult && isCorrect && (
                      <i className="ri-check-line text-xl text-green-600"></i>
                    )}
                    {showResult && isSelected && !isCorrect && (
                      <i className="ri-close-line text-xl text-red-600"></i>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Explanation */}
          {showExplanation && (
            <div className="bg-gradient-to-br from-fuchsia-50 to-purple-50 border-l-4 border-fuchsia-500 p-6 rounded-lg mb-6">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-lightbulb-line text-fuchsia-600"></i>
                Explanation
              </h4>
              <p className="text-gray-700 leading-relaxed">{question.explanation}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className="px-6 py-3 rounded-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Previous
            </button>

            {!showExplanation ? (
              <button
                onClick={handleSubmitAnswer}
                disabled={selectedAnswer === null}
                className="flex-1 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white px-8 py-3 rounded-xl font-semibold hover:from-fuchsia-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Submit Answer
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="flex-1 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white px-8 py-3 rounded-xl font-semibold hover:from-fuchsia-600 hover:to-purple-600 transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                {currentQuestion < expressionIdeasQuestions.length - 1 ? (
                  <>
                    Next Question
                    <i className="ri-arrow-right-line ml-2"></i>
                  </>
                ) : (
                  <>
                    View Results
                    <i className="ri-trophy-line ml-2"></i>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}